import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Camera, Sparkles, Send, PhoneCall, MessageSquare, ArrowLeft, ArrowRight, X } from "lucide-react";

export const HOW_STEPS = [
  { icon: FileText, title: "Request Service", body: "No account needed. Tap “Request without an account” and tell us the type of work and property address." },
  { icon: Camera, title: "Add Photos & Details", body: "Snap or upload photos of the property and add a few notes. The more we see, the more accurate your estimate." },
  { icon: Sparkles, title: "Get an Instant AI Estimate", body: "Our AI reads your photos and details and builds an itemized estimate using national & standard pricing — right on screen." },
  { icon: Send, title: "Submit for Review", body: "Happy with it? Submit your request. Your estimate and photos are sent straight to our dispatch team." },
  { icon: PhoneCall, title: "We Confirm Your Quote", body: "Our team reviews everything and confirms your final price. We’ll email and call you back to lock in the details." },
  { icon: MessageSquare, title: "Track & Chat", body: "Create a free account any time to track job progress, view the photo gallery and chat with your crew." },
];

export function HowItWorksSteps({ compact = false }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {HOW_STEPS.map((s, i) => {
        const Icon = s.icon;
        return (
          <div key={s.title} data-testid={`hiw-step-${i}`} className="flex gap-3 bg-[#141417] border-2 border-zinc-800 p-4 animate-rise" style={{ animationDelay: `${i * 50}ms` }}>
            <div className="shrink-0">
              <div className="w-10 h-10 bg-[#FF5722] text-black border-2 border-black shadow-[3px_3px_0px_#000] flex items-center justify-center"><Icon size={20} /></div>
              <div className="font-mono text-[10px] text-zinc-600 mt-1 text-center">0{i + 1}</div>
            </div>
            <div>
              <h3 className="font-head font-bold uppercase text-white text-sm tracking-wide">{s.title}</h3>
              {!compact && <p className="text-zinc-400 text-sm mt-1 leading-snug">{s.body}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function HowItWorksModal({ open, onClose }) {
  const [i, setI] = useState(0);
  const step = HOW_STEPS[i];
  const Icon = step.icon;
  const last = i === HOW_STEPS.length - 1;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setI(0); } }}>
      <DialogContent className="bg-[#09090b] border-2 border-[#FF5722] text-white rounded-none max-w-md p-0 gap-0" data-testid="hiw-modal">
        <DialogTitle className="sr-only">How it works</DialogTitle>
        <DialogDescription className="sr-only">Step-by-step guide to requesting service and getting an instant estimate.</DialogDescription>
        <div className="flex items-center justify-between px-5 py-3 border-b-2 border-zinc-800">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5722]">How it works · {i + 1}/{HOW_STEPS.length}</div>
          <button data-testid="hiw-close" onClick={() => { onClose(); setI(0); }} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-[#FF5722] text-black border-2 border-black shadow-[4px_4px_0px_#000] flex items-center justify-center"><Icon size={30} /></div>
          <h2 className="font-head font-black uppercase text-2xl text-white tracking-tight mt-5">{step.title}</h2>
          <p className="text-zinc-400 mt-3 leading-relaxed">{step.body}</p>
        </div>
        <div className="flex items-center gap-1.5 justify-center pb-4">
          {HOW_STEPS.map((s, idx) => <span key={s.title} className={`h-1.5 w-6 ${idx === i ? "bg-[#FF5722]" : "bg-zinc-800"}`} />)}
        </div>
        <div className="flex gap-2 p-5 pt-0">
          <button data-testid="hiw-prev" disabled={i === 0} onClick={() => setI(i - 1)} className="flex-1 py-2.5 border-2 border-zinc-800 text-zinc-300 font-head font-bold uppercase text-sm disabled:opacity-30 flex items-center justify-center gap-1"><ArrowLeft size={15} /> Back</button>
          {last ? (
            <button data-testid="hiw-done" onClick={() => { onClose(); setI(0); }} className="flex-1 py-2.5 bg-[#FF5722] text-black border-2 border-black shadow-[3px_3px_0px_#000] font-head font-bold uppercase text-sm">Got it</button>
          ) : (
            <button data-testid="hiw-next" onClick={() => setI(i + 1)} className="flex-1 py-2.5 bg-[#FF5722] text-black border-2 border-black shadow-[3px_3px_0px_#000] font-head font-bold uppercase text-sm flex items-center justify-center gap-1">Next <ArrowRight size={15} /></button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
